import { SpinnerIcon } from "@/components/icons";
import React, { useState, useRef, useEffect } from 'react';

interface CaptionEditorProps {
  initialCaption: string;
  onSave: (caption: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CaptionEditor: React.FC<CaptionEditorProps> = ({
  initialCaption,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const [caption, setCaption] = useState(initialCaption);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus and select all text when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      // Auto-resize textarea
      adjustTextareaHeight();
    }
  }, []);

  // Adjust textarea height based on content
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(60, textarea.scrollHeight)}px`;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaption(e.target.value);
    adjustTextareaHeight();
  };

  const handleSave = async () => {
    await onSave(caption.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const characterCount = caption.length;
  const maxCharacters = 2200; // Instagram's limit
  const isOverLimit = characterCount > maxCharacters;

  return (
    <div className="space-y-4">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={caption}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Scrivi la tua caption qui, non ti preoccupare la miglioreremo se c'è bisogno"
          className={`w-full p-4 border rounded-xl resize-none focus:outline-hidden focus:ring-2 focus:border-transparent transition-all duration-200 bg-gray-50/50 ${
            isOverLimit
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-200 focus:ring-blue-500'
          }`}
          disabled={isLoading}
          style={{ minHeight: '80px' }}
        />
        
        {/* Character counter */}
        <div className={`absolute bottom-2 right-2 text-xs px-2 py-1 rounded ${
          isOverLimit
            ? 'bg-red-100 text-red-700'
            : characterCount > maxCharacters * 0.9
            ? 'bg-amber-100 text-amber-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          {characterCount.toLocaleString()}/{maxCharacters.toLocaleString()}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end">
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2.5 text-sm text-gray-600 bg-gray-100/80 rounded-xl hover:bg-gray-200/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || isOverLimit}
            className="px-6 py-2.5 text-sm bg-linear-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <SpinnerIcon className="w-4 h-4" />
              </div>
            ) : (
              'Salva'
            )}
          </button>
        </div>
      </div>

      {/* Validation messages */}
      {isOverLimit && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-3 font-medium">
          La caption supera il limite di caratteri consigliato. Per favore accorciala per un miglior engagement.
        </div>
      )}
    </div>
  );
};